import { CHILD_WORDS, PARENT_OF_WORDS } from "../member-resolver.js";
import type { AiSlot, AiSlotFromSentence } from "../registry/schema.js";
import { isFirstPersonPronoun } from "./member-slot.js";
import { normalizeEnumKey, sameListedWord, sameWord, wordsOf, type Word } from "./vi-words.js";
import {
  SLOT_RESOLUTION_REASON,
  slotCandidates,
  slotNone,
  slotResolved,
  type SlotResolution,
} from "./slot-resolution.js";

/**
 * 🔴 US-AIV2-0054b (@UC0176 · QĐ PO 19/09 §A-BIS) — VỊ-TỪ "THUỘC TẬP ĐÓNG", MỘT CHỖ DUY NHẤT.
 *
 * Từ 19/09, tập đóng `choices` không chỉ là luật RESOLVE nữa — nó là **chốt an toàn** của ô phân loại: phép neo
 * của Người phiên dịch (`anchorSpansToSentence`) thôi đòi giá trị enum phải có trong CÂU, và đòi nó phải có trong
 * CATALOG. Hai chỗ hỏi cùng một câu hỏi, nên câu hỏi chỉ được viết MỘT lần (MEMORY: *"một luật rải ở 12 bản chép
 * ⇒ thêm vế thứ N sót 11 chỗ"*). Nhờ vậy giá trị mà phép neo giữ lại luôn khớp CHÍNH XÁC ở bước đầu của
 * `resolveEnumSlot` — không có khe nào cho một giá trị "qua được cổng nhưng resolver không hiểu".
 *
 * Trả về **chính giá trị của catalog** (không phải chữ người/model gõ) hoặc `null`. `null` khi: rỗng · 0 khớp ·
 * ≥2 giá trị cùng dạng chuẩn hoá (mơ hồ ⇒ hỏi, không chọn hộ).
 */
export function matchEnumChoice(text: string | null | undefined, choices: readonly string[]): string | null {
  const raw = (text ?? "").trim();
  if (raw === "" || choices.length === 0) return null;
  const verbatim = choices.find((c) => c === raw);
  if (verbatim !== undefined) return verbatim;
  const needle = normalizeEnumKey(raw);
  if (needle === "") return null;
  const hits = choices.filter((c) => normalizeEnumKey(c) === needle);
  return hits.length === 1 ? hits[0]! : null;
}

// `normalizeEnumKey` + bộ tách TỪ (`wordsOf` · `sameWord`) ở `vi-words.ts` (US-AIV2-0112 PCD-HARDEN tách ra cho file này dưới trần 800 dòng).
export { normalizeEnumKey } from "./vi-words.js";

/**
 * 🔴 US-AIV2-0022 — ô `enum`: chữ → MỘT giá trị trong `slot.choices` của CHÍNH Ô đó trong Sổ (0011).
 *
 * Tập giá trị là DỮ LIỆU của Sổ, không phải hằng ở đây: thêm lựa chọn cho một trang = sửa trang (@UC0157), resolver
 * không đổi. Khớp bằng cùng luật token của `named-ref-slot` sau khi đổi `_`/`-` thành khoảng trắng ("phu_huynh" ≡
 * "phụ huynh" ≡ "nhóm phụ huynh"). 0 khớp ⇒ mời chọn TOÀN BỘ `choices`; ≥2 ⇒ hỏi kèm đúng các giá trị khớp.
 * Giá trị bằng CHÍNH XÁC (sau chuẩn hoá) thắng giá trị chỉ chứa token: "công ty con" ⇒ `cong_ty_con`, không hỏi giữa `cong_ty`/`cong_ty_con` (#815 M2).
 * Ô không khai `choices` (validator Sổ đã chặn) ⇒ `none/empty_scope` — fail-closed, không đoán tập.
 */
export function resolveEnumSlot(
  text: string | null | undefined,
  slot: Pick<AiSlot, "choices" | "synonyms" | "fallback" | "guardedChoices" | "fromSentence">,
  opts: EnumResolveOptions = {},
): SlotResolution<string> {
  const choices = slot.choices ?? [];
  if (choices.length === 0) return slotNone(SLOT_RESOLUTION_REASON.EMPTY_SCOPE);
  const all = choices.map((c) => ({ value: c, label: c }));
  // C10: mặc định CHỈ khi caller cho phép (trang đọc) VÀ Sổ khai một giá trị thuộc tập đóng — không thì hỏi như cũ.
  const fallback = opts.allowFallback === true && slot.fallback !== undefined && choices.includes(slot.fallback) ? slot.fallback : null;
  const orFallback = (reason: typeof SLOT_RESOLUTION_REASON.MISSING | typeof SLOT_RESOLUTION_REASON.UNMATCHED): SlotResolution<string> =>
    fallback !== null ? slotResolved(fallback, fallback) : slotCandidates(reason, all);
  const raw = (text ?? "").trim();
  if (raw === "") return orFallback(SLOT_RESOLUTION_REASON.MISSING);
  // 🔴 US-AIV2-0112 PCD-HARDEN (@UC0199, lỗ #1036 · review #1076 H1): ô quyết định (`money-received`) + chữ NÊU TÊN người ⇒ không chọn gì, TRƯỚC mọi
  // nhánh (nguyên văn · khuôn · nhãn · đồng nghĩa · phủ định) và cho CẢ HAI lựa chọn. Chỉ Người làm (biết người trả của lời báo đang xét) bỏ được tên.
  if (slot.fromSentence === "money-received" && decisionPersonRefs(raw, slot).names.length > 0) return slotCandidates(SLOT_RESOLUTION_REASON.UNMATCHED, all);
  // 🔴 lô Sổ 2 (review #1036): ô khai `guardedChoices` + chữ phủ định/nghi vấn ⇒ nhánh RIÊNG, xét TRƯỚC mọi nhánh khớp (nhãn · nguyên văn · đồng nghĩa).
  if ((slot.guardedChoices?.length ?? 0) > 0 && hasNegationOrDoubt(raw)) return resolveHedged(raw, choices, all, slot);
  // 🔴 US-AIV2-0112 H02 (@UC0220 @UC0192): ô khai `fromSentence` (lưới đỡ suy lựa chọn từ câu) ⇒ chữ phủ định/nghi vấn KHÔNG chọn gì, ở MỌI nhánh
  // bên dưới (nguyên văn · nhãn · đồng nghĩa) và mọi lượt: lượt đầu phép neo đưa CẢ CÂU (`enumSentenceNeedsWholeView`), lượt trả lời chữ là cả câu
  // trả lời. Hỏi kèm MỌI lựa chọn — nút gửi đúng chữ nhãn (không còn dấu phủ định) ⇒ chọn được.
  // Bước 7: + phủ quyết theo NGHĨA ô ("mỗi tháng 2 lần", "cựu học sinh") — CÙNG vị-từ với lưới câu và phép neo.
  if (fromSentenceBlocks(slot, raw)) return slotCandidates(SLOT_RESOLUTION_REASON.UNMATCHED, all);
  // Bằng CHÍNH XÁC (nguyên văn, hoặc sau chuẩn hoá bỏ dấu/`_`) — CÙNG vị-từ mà phép neo dùng làm chốt an toàn.
  const exact = matchEnumChoice(raw, choices);
  if (exact !== null) return slotResolved(exact, exact);
  // 🔴 US-AIV2-0112 H08(a) (@UC0199, QĐ PO 30/09 P9): ô khai nghĩa KHẲNG ĐỊNH (`money-received`) + chữ theo khuôn ĐÓNG "tiền (của X) đã về" ⇒
  // lựa chọn DUY NHẤT được chốt ("xác nhận"). Chữ phủ định/nghi vấn đã rẽ nhánh `resolveHedged` ở trên — không bao giờ tới đây.
  const affirmed = affirmedChoiceOf(slot, raw);
  if (affirmed !== null) return slotResolved(affirmed, affirmed);

  const needle = wordsOf(raw);
  if (needle.length === 0) return orFallback(SLOT_RESOLUTION_REASON.UNMATCHED);
  // 🔴 review #1070 HIGH-ts — nhánh "chứa" so TỪ bằng `sameWord` (H1: hai phía có dấu ⇒ phải khớp dấu), không còn so token bỏ dấu: lượt trả lời
  // không có span ⇒ chữ = CẢ câu ("thu hằng tháng cho quỹ lớp mình") ⇒ bản cũ thấy "quỹ" ≡ "quý" ⇒ mơ hồ ⇒ hỏi lại lần hai.
  let uncertain = false;
  const hits = choices.filter((c) => {
    const label = wordsOf(c);
    if (label.length === 0) return false;
    // Chữ chứa trọn giá trị ("nhóm phụ huynh" ⊃ "phu huynh") hoặc giá trị chứa trọn chữ.
    const used = label.every((l) => needle.some((n) => sameWord(n, l)))
      ? needle.filter((n) => label.some((l) => sameWord(n, l)))
      : needle.every((n) => label.some((l) => sameWord(n, l)))
        ? needle
        : null;
    if (used === null) return false;
    // re-review #1070 LOW-1: nhãn xuất hiện TRỌN, liền mạch ("thu hang quy") ⇒ không phải "khớp nhờ chữ hai nghĩa" (không thể là "hằng quỹ").
    if (used.some(isAmbiguousPlain) && phraseStarts(needle, label).length === 0) uncertain = true;
    return true;
  });
  // M2: một lần khớp nhờ chữ không dấu hai nghĩa ("quy") ⇒ không đủ chắc ⇒ hỏi kèm MỌI lựa chọn.
  if (uncertain) return slotCandidates(SLOT_RESOLUTION_REASON.UNMATCHED, all);
  if (hits.length === 1) return slotResolved(hits[0]!, hits[0]!);
  if (hits.length === 0) {
    const bySynonym = matchEnumSynonyms(needle, choices, slot.synonyms);
    if (bySynonym === null) return slotCandidates(SLOT_RESOLUTION_REASON.UNMATCHED, all);
    if (bySynonym.length === 1) return slotResolved(bySynonym[0]!, bySynonym[0]!);
    if (bySynonym.length === 0) return orFallback(SLOT_RESOLUTION_REASON.UNMATCHED);
    return slotCandidates(
      SLOT_RESOLUTION_REASON.AMBIGUOUS,
      bySynonym.map((c) => ({ value: c, label: c })),
    );
  }
  return slotCandidates(
    SLOT_RESOLUTION_REASON.AMBIGUOUS,
    hits.map((c) => ({ value: c, label: c })),
  );
}

/**
 * Chữ phủ định/nghi vấn ở ô có `guardedChoices`: CHỈ lựa chọn KHÔNG chốt được chọn, và chỉ khi nhãn/cụm đồng nghĩa của nó xuất hiện KHÔNG ngay
 * sau từ phủ định. Đúng một ⇒ chọn; ≥2 ⇒ hỏi giữa chúng; 0 ⇒ hỏi kèm MỌI lựa chọn (nút "xác nhận" gửi đúng chữ nhãn ⇒ không còn dấu phủ định ⇒ chọn được).
 */
function resolveHedged(
  raw: string,
  choices: readonly string[],
  all: readonly { readonly value: string; readonly label: string }[],
  slot: Pick<AiSlot, "synonyms" | "guardedChoices">,
): SlotResolution<string> {
  const guarded = new Set(slot.guardedChoices ?? []);
  const words = wordsOf(raw);
  const phrasesOf = (c: string): readonly string[] => [c, ...(slot.synonyms !== undefined && Object.hasOwn(slot.synonyms, c) ? slot.synonyms[c]! : [])];
  const hits = choices.filter(
    (c) =>
      !guarded.has(c) &&
      phrasesOf(c).some((p) => {
        const run = normalizeEnumKey(p).split(" ").filter((t) => t !== "");
        return run.length > 0 && containsRunNotNegated(words, run);
      }),
  );
  if (hits.length === 1) return slotResolved(hits[0]!, hits[0]!);
  if (hits.length === 0) return slotCandidates(SLOT_RESOLUTION_REASON.UNMATCHED, all);
  return slotCandidates(SLOT_RESOLUTION_REASON.AMBIGUOUS, hits.map((c) => ({ value: c, label: c })));
}

/** Tuỳ chọn của `resolveEnumSlot` — caller (bộ phân phối `resolveSlot`) quyết theo bậc trang, hàm này không đoán. */
export interface EnumResolveOptions {
  /** `true` ⇒ ô trống / cụm không khớp gì ⇒ `slot.fallback` (nếu Sổ khai). Vắng ⇒ hỏi như cũ (fail-closed). */
  readonly allowFallback?: boolean;
}

/**
 * 🔴 US-AIV2-0111 C10 (@UC0205 @UC0206 @UC0207) — ĐỒNG NGHĨA của Sổ: cụm người dùng chứa TRỌN một cụm đồng nghĩa, theo
 * **từ nguyên vẹn liên tiếp** (không chuỗi con: đồng nghĩa "chi" không khớp "chiều"), sau cùng phép chuẩn hoá với lựa chọn.
 * Nhiều lựa chọn khớp ⇒ lựa chọn có cụm khớp DÀI NHẤT thắng ("chi bao nhiêu" 3 từ thắng "chi" 1 từ); hoà ở độ dài lớn nhất ⇒
 * trả CẢ các lựa chọn hoà (caller hỏi giữa đúng chúng, không chọn hộ). Trả về giá trị CỦA CATALOG, theo thứ tự `choices`.
 */
function matchEnumSynonyms(
  needle: readonly Word[],
  choices: readonly string[],
  synonyms: Readonly<Record<string, readonly string[]>> | undefined,
): string[] | null {
  if (synonyms === undefined) return [];
  let uncertain = false;
  const bestByChoice = choices.map((choice) => {
    const phrases = Object.hasOwn(synonyms, choice) ? synonyms[choice]! : [];
    let best = 0;
    for (const phrase of phrases) {
      const run = wordsOf(phrase);
      const starts = phraseStarts(needle, run);
      if (starts.some((i) => needle.slice(i, i + run.length).some(isAmbiguousPlain))) uncertain = true;
      if (run.length > best && starts.length > 0) best = run.length;
    }
    return best;
  });
  // review #1070 M2 — khớp nhờ chữ không dấu hai nghĩa ⇒ `null` (caller hỏi kèm mọi lựa chọn).
  if (uncertain) return null;
  const top = Math.max(0, ...bestByChoice);
  if (top === 0) return [];
  return choices.filter((_, i) => bestByChoice[i] === top);
}

/** Mọi vị trí bắt đầu của `run` (liền mạch) trong `tokens`. */
function runStarts(tokens: readonly string[], run: readonly string[]): number[] {
  const out: number[] = [];
  for (let start = 0; start + run.length <= tokens.length; start++) {
    if (run.every((t, k) => tokens[start + k] === t)) out.push(start);
  }
  return out;
}

/**
 * 🔴 US-AIV2-0112 lô Sổ 2 (review #1036 HIGH-1/2/MED-1 · @UC0199 · CLAUDE.md §3.7) — CHỐT PHỦ ĐỊNH/NGHI VẤN cho ô khai `guardedChoices`.
 *
 * Bản đầu (lô Sổ 2 lượt 1) chỉ nhìn MỘT token ngay trước cụm đồng nghĩa, áp cho MỌI ô, và nhánh khớp NHÃN chạy trước không có chốt ⇒ reviewer
 * đo được 19 câu ("nhận được chưa", "hình như đã nhận", "đâu có nhận được", "k nhận được", "chưa xác nhận", "không duyệt" ở EXP-APPROVE…) ra
 * lựa chọn KHẲNG ĐỊNH; còn trang ĐỌC đổi nghĩa (READ-PEOPLE-MINE "toi chua bo phieu khoan nao"). Luật nay:
 *  · CHỈ ô khai `guardedChoices` (trang ghi, validator gác) — ô khác y như develop;
 *  · chữ có BẤT KỲ dấu hiệu phủ định / nghi vấn / ước đoán (tập dưới, hoặc "?") ⇒ lựa chọn trong `guardedChoices` KHÔNG BAO GIỜ được chọn, ở
 *    MỌI nhánh (chữ trùng nguyên văn, nhãn, đồng nghĩa); lựa chọn KHÔNG chốt (từ chối) vẫn chọn được — trừ khi chính cụm của nó đứng ngay
 *    sau một từ phủ định ("đừng từ chối") ⇒ hỏi. Cụm tự mang phủ định ("không duyệt" ⇒ từ chối) khớp như thường.
 * Chiều sai nào cũng là HỎI LẠI — không có đường nào biến câu phủ định/nghi vấn thành "xác nhận".
 */
const NEGATION_TOKENS: ReadonlySet<string> = new Set(["chua", "khong", "ko", "k", "kg", "khg", "hong", "chang", "cha", "dung", "dau"]);
const DOUBT_PHRASES: readonly (readonly string[])[] = [["hinh", "nhu"], ["co", "le"], ["chac"], ["khong", "biet"], ["nghe", "noi"], ["a"], ["ha"], ["nhi"], ["sao"], ["ho"], ["hu"], ["chu"]];
const TRAILING_ONLY: ReadonlySet<string> = new Set(["a", "ha", "nhi", "sao", "ho", "hu", "chu"]);

/**
 * 🔴 FU-1036 (follow-up review #1036 LOW-2 · @UC0199) — CÁI ĐƯỢC THA, liệt kê TƯỜNG MINH theo chữ CÒN DẤU. Khoá: dạng bỏ dấu (khớp một từ
 * trong hai tập trên). Bỏ dấu làm "Dũng"/"đúng" ≡ "dung" (đừng), "Hồng" ≡ "hong" (hông), "đầu" ≡ "dau" (đâu), "ạ" ≡ "a" (à) ⇒ câu KHẲNG ĐỊNH
 * bị hỏi lại oan. Chỉ đúng các chữ CÒN DẤU dưới đây được tha; mọi dạng khác — dạng KHÔNG dấu ("dung", "hong", "dau", "a": không phân biệt được
 * ⇒ không đoán), dạng phủ định/nghi vấn (đừng · dừng · hông · hổng · đâu · à · á), gõ sai dấu — VẪN là phủ định/nghi vấn ⇒ hỏi lại. Tha theo
 * danh sách chứ không bắt theo danh sách: sót một chữ ⇒ thêm một chạm, không bao giờ ra "xác nhận". Các từ một nghĩa phủ định
 * (chua · khong · ko · k · kg · khg) không có mục ở đây.
 */
const NOT_NEGATION_ACCENTED: ReadonlyMap<string, ReadonlySet<string>> = new Map([
  ["dung", new Set(["dũng", "đúng", "dùng", "dụng"])],
  ["hong", new Set(["hồng"])],
  ["dau", new Set(["đầu", "dầu", "dâu", "dấu", "đậu"])],
  ["a", new Set(["ạ"])],
]);

/**
 * 🔴 FU-1036 LOW-1 — trợ từ hỏi cuối câu MỚI ("hở" · "hử" · "chứ"): CHỈ đúng chữ còn dấu này mới tính. Khác bảng tha ở trên vì dạng KHÔNG dấu của
 * chúng là chữ thường ngày trong nghiệp vụ tiền: "dong ho" = đóng HỘ (phát lại bản ghi eval: 4 câu PAY-ON-BEHALF gõ không dấu sẽ bị hỏi lại
 * oan), "nhan ho" = nhận hộ, "cua chu" = của chú. Dạng không dấu giữ ĐÚNG như develop (không tính) — không nới gì so với trước.
 */
const DOUBT_ONLY_ACCENTED: ReadonlyMap<string, ReadonlySet<string>> = new Map([
  ["ho", new Set(["hở"])],
  ["hu", new Set(["hử"])],
  ["chu", new Set(["chứ"])],
]);

/** Hai cụm là MỘT cụm theo `sameWord` từng từ (validator Sổ dùng: đồng nghĩa trùng chính lựa chọn là thừa). */
export function sameEnumPhrase(a: string, b: string): boolean {
  const wa = wordsOf(a);
  const wb = wordsOf(b);
  return wa.length === wb.length && wa.length > 0 && wa.every((w, i) => sameWord(w, wb[i]!));
}

/**
 * 🔴 review #1070 M2 + LOW — dạng KHÔNG dấu của HAI chữ nghiệp vụ: "quy" = quý (chu kỳ) HOẶC quỹ (danh từ câu nào cũng có). `sameWord` vẫn cho chữ
 * này khớp (nới như mọi chữ không dấu), nhưng một lần khớp CÓ dùng nó thì không đủ chắc ⇒ HỎI, ở cả ba chỗ đoán theo cụm: nhánh "chứa nhãn" và
 * nhánh đồng nghĩa của `resolveEnumSlot`, và lưới câu `findEnumChoiceInSentence` — trừ khi NHÃN xuất hiện trọn, liền mạch (re-review LOW-1:
 * "thu hang quy" = hằng quý). KHÔNG áp ở bước BẰNG CHÍNH XÁC (`matchEnumChoice`, chung với
 * phép neo): gõ trọn nhãn "hang quy" không thể đọc thành "hằng quỹ". Tập ĐÓNG, chỉ THÊM khi đo được câu thật.
 */
const AMBIGUOUS_PLAIN_KEYS: ReadonlySet<string> = new Set(["quy"]);
const isAmbiguousPlain = (w: Word): boolean => w.plain && AMBIGUOUS_PLAIN_KEYS.has(w.key);

/** Cụm `phrase` xuất hiện LIỀN MẠCH trong câu (cùng `sameWord`) — cho phủ quyết ngữ cảnh của lưới câu (`enum-fallback.ts`). */
export function sentenceHasEnumPhrase(sentence: string, phrase: string): boolean {
  return phraseStarts(wordsOf(sentence), wordsOf(phrase)).length > 0;
}

/** Mọi vị trí (chỉ số từ) mà cụm `run` xuất hiện LIỀN MẠCH trong `words`, so từng từ bằng `sameWord`. */
function phraseStarts(words: readonly Word[], run: readonly Word[]): number[] {
  const out: number[] = [];
  if (run.length === 0) return out;
  for (let i = 0; i + run.length <= words.length; i++) {
    if (run.every((w, k) => sameWord(words[i + k]!, w))) out.push(i);
  }
  return out;
}

/** Từ `w` mang dạng bỏ dấu `key` của tập phủ định/nghi vấn — trừ khi chữ còn dấu của nó được tha (hoặc, với trợ từ chỉ-có-dấu, không phải đúng chữ đó). */
function isMarker(w: Word, key: string): boolean {
  if (w.key !== key) return false;
  const only = DOUBT_ONLY_ACCENTED.get(key);
  if (only !== undefined) return only.has(w.accented);
  return !(NOT_NEGATION_ACCENTED.get(key)?.has(w.accented) ?? false);
}

function isNegationWord(w: Word): boolean {
  return NEGATION_TOKENS.has(w.key) && isMarker(w, w.key);
}

/**
 * 🔴 AI-INTERP N2 (@UC0267) — từ phủ định mà NGỮ CẢNH hai tiếng chứng minh là chữ khác (cùng triết lý FU-1036: liệt kê cái được tha, sót ⇒ thêm một
 * chạm). Đo từ câu thật của lưới ô tiền S19 khi nó bắt đầu đọc vị-từ này:
 *  · (CHỈ khi caller bật `plainDungCompound` — lưới ô tiền S19) "dung" gõ KHÔNG dấu trong cụm từ ghép: "nội/sử/tiêu/ứng/vật/áp/trung/tuyển dung" ·
 *    "dung cụ" ("hon nua phai mua them dung cu"). 🔴 vá review bảo mật HIGH-A / M1: bản trước xét dạng bỏ dấu ở vị-từ CHUNG ⇒ tha cả "đừng"/"dừng" CÓ
 *    dấu («khoản tạm ứng đừng xác nhận» ⇒ "xác nhận" · «Trung đừng đóng») và "dung cu" không dấu (= "đừng cứ") ở ô quyết định («dung cu the duyet» ⇒
 *    "duyệt"). Ô quyết định / lưới mở đợt / H02 / H08(a) không cần miễn trừ này ⇒ vị-từ chung giữ "dung" nào cũng là phủ định (như develop);
 *  · (vá review MED-2) "chua" gõ ĐÚNG (không dấu là chính tả của "chua") ngay sau "sữa" gõ ĐÚNG dấu ⇒ "sữa chua"; "sữa chưa" · "sua chua" (cả hai không
 *    dấu — không phân biệt được) vẫn là phủ định.
 * (vá review LOW-1) "k" sau một số là đơn vị nghìn CHỈ trong câu tiền — miễn trừ đó ở lưới S19 (`money-fallback.ts`), KHÔNG ở vị-từ chung: «Lan đợt 2 k
 * nhận được tiền» · «lop 5 k dong» phải giữ chốt phủ định cho H08(a) / CHO-CON.
 */
// 🔴 vá re-review LOW (FU-1036 — không dấu, không phân biệt được ⇒ không đoán): "trung"/"tuyen" bỏ (tên Trung · Tuyền: «Trung dung chi 200k» = "Trung đừng
// chi"); "nội" sau "ông/bà" là người («ba noi dung chi 200k» = "bà nội đừng chi"); "dung cu" chỉ tha khi "cụ" CÓ dấu, hoặc "dung" đứng sau động từ/lượng từ
// mua · thêm · các ("mua thêm dụng cụ" — sau các chữ đó "đừng" sai ngữ pháp); «dung cu chi 200k» (= "đừng cứ chi") ⇒ phủ định.
const DUNG_COMPOUND_BEFORE: ReadonlySet<string> = new Set(["noi", "su", "tieu", "ung", "vat", "ap"]);
const DUNG_CU_LEADS: ReadonlySet<string> = new Set(["mua", "them", "cac"]);
const GRANDPARENT_KEYS: ReadonlySet<string> = new Set(["ong", "ba"]);
/** "dung" (gõ không dấu) ở vị trí `i` là chữ trong từ ghép "nội dung" · "sử dụng" · "dụng cụ"… — chỉ khi ngữ cảnh loại trừ được "đừng". */
function isPlainDungCompound(words: readonly Word[], i: number): boolean {
  const prev = words[i - 1]?.key ?? "";
  if (prev === "noi" && GRANDPARENT_KEYS.has(words[i - 2]?.key ?? "")) return false;
  if (DUNG_COMPOUND_BEFORE.has(prev)) return true;
  const next = words[i + 1];
  return next?.key === "cu" && (next.accented === "cụ" || DUNG_CU_LEADS.has(prev));
}

/** Tuỳ chọn của `hasNegationOrDoubt`. Mặc định (mọi lưới/ô trừ S19): không miễn trừ "dung" nào. */
export interface NegationReadOptions {
  /** CHỈ lưới ô tiền S19: "dung" gõ KHÔNG dấu trong từ ghép ("nội dung" · "dung cụ") không là "đừng". "đừng"/"dừng" có dấu KHÔNG BAO GIỜ được tha. */
  readonly plainDungCompound?: boolean;
}

function negationExemptByContext(words: readonly Word[], i: number, opts: NegationReadOptions): boolean {
  const w = words[i]!;
  if (w.key === "dung") return opts.plainDungCompound === true && w.plain && isPlainDungCompound(words, i);
  if (w.key === "chua") return w.accented === "chua" && words[i - 1]?.accented === "sữa";
  return false;
}

/** Chữ có dấu hiệu phủ định / nghi vấn / ước đoán (theo từ nguyên vẹn; từ trùng dạng bỏ dấu với một từ thường xét trên chữ còn dấu) — hoặc dấu "?". */
export function hasNegationOrDoubt(text: string, opts: NegationReadOptions = {}): boolean {
  if (text.includes("?")) return true;
  const words = wordsOf(text);
  if (words.some((w, i) => isNegationWord(w) && !negationExemptByContext(words, i, opts))) return true;
  const last = words.at(-1);
  return DOUBT_PHRASES.some((p) =>
    p.length === 1 && TRAILING_ONLY.has(p[0]!)
      ? last !== undefined && isMarker(last, p[0]!)
      : runStarts(
          words.map((w) => w.key),
          p,
        ).some((i) => p.every((k, j) => isMarker(words[i + j]!, k))),
  );
}

/** Cụm `run` có ít nhất một lần xuất hiện KHÔNG đứng ngay sau từ phủ định (CÙNG vị-từ `isNegationWord` với `hasNegationOrDoubt`). */
function containsRunNotNegated(words: readonly Word[], run: readonly string[]): boolean {
  return runStarts(
    words.map((w) => w.key),
    run,
  ).some((i) => i === 0 || !isNegationWord(words[i - 1]!));
}

/** Một lựa chọn tìm thấy trong CÂU: giá trị catalog + đoạn `[start,end)` của chuỗi NFC nơi nhãn/cụm đồng nghĩa của nó xuất hiện. */
export interface EnumSentenceMatch {
  readonly choice: string;
  readonly start: number;
  readonly end: number;
}

/**
 * 🔴 US-AIV2-0112 H02 (@UC0220 @UC0192) — TÌM LỰA CHỌN TRONG CẢ CÂU (nửa "đọc chữ" của lưới đỡ `fillEnumSlotsFromSentence`; nửa kia — opt-in,
 * Phiếu chưa gạch ô, chốt phủ định, neo-vào-câu — ở `interpreter/enum-fallback.ts`).
 *
 * Luật: mỗi lựa chọn khớp khi NHÃN của nó hoặc một cụm `synonyms` của nó xuất hiện LIỀN MẠCH theo TỪ nguyên vẹn (`sameWord`). KHÔNG dùng phép "chứa trọn tập từ" của `resolveEnumSlot`: trên cả câu phép đó khớp rác ("thu quỹ hằng tháng" chứa đủ {hằng, quý}
 * rải rác). So từ bằng `sameWord` (dấu phải khớp khi cả hai phía có dấu); khớp nhờ chữ không dấu hai nghĩa ⇒ `null`. Một lần xuất hiện nằm TRỌN trong lần xuất hiện dài hơn của lựa chọn KHÁC thì bị nuốt ("du lịch gia đình" ⊃ "gia đình", "họp lớp" ⊃
 * "lớp") — cùng tinh thần "cụm dài nhất thắng" của đồng nghĩa, nhưng chỉ khi CHỒNG vị trí. Còn lại ĐÚNG MỘT lựa chọn ⇒ trả lựa chọn đó + lần
 * xuất hiện đầu; 0 hoặc ≥2 ⇒ `null` (hỏi, không chọn hộ). Chữ chung một mình ("tháng", "quý", "con", "đội") không phải cụm của Sổ ⇒ không khớp.
 */
export function findEnumChoiceInSentence(sentence: string, slot: Pick<AiSlot, "choices" | "synonyms">): EnumSentenceMatch | null {
  const kept = enumSentenceHits(sentence, slot);
  if (kept === null || new Set(kept.map((h) => h.choice)).size !== 1) return null;
  return kept.reduce((a, b) => (b.start < a.start ? b : a));
}

/**
 * 🔴 US-AIV2-0113 M09 (@UC0206) — MỌI lựa chọn mà CẢ câu nhắc tới, theo thứ tự xuất hiện đầu tiên. CÙNG phép đọc chữ với
 * `findEnumChoiceInSentence` (nhãn/đồng nghĩa của Sổ, theo TỪ liền mạch, cụm dài nuốt cụm ngắn chồng vị trí) — chỉ khác ở chỗ KHÔNG đòi đúng
 * một. Khớp nhờ chữ không dấu hai nghĩa ⇒ `null` (không đủ chắc để nói câu hỏi gì). Dùng cho câu GHÉP HAI Ý (`compound-choices.ts`).
 */
export function enumChoicesInSentence(sentence: string, slot: Pick<AiSlot, "choices" | "synonyms">): readonly string[] | null {
  const kept = enumSentenceHits(sentence, slot);
  if (kept === null) return null;
  const firstAt = new Map<string, number>();
  for (const h of kept) firstAt.set(h.choice, Math.min(firstAt.get(h.choice) ?? h.start, h.start));
  return [...firstAt.entries()].sort((a, b) => a[1] - b[1]).map(([c]) => c);
}

/** Lần xuất hiện của nhãn/cụm đồng nghĩa trong câu, sau khi cụm dài nuốt cụm ngắn của lựa chọn KHÁC; `null` = khớp nhờ chữ không dấu hai nghĩa. */
function enumSentenceHits(sentence: string, slot: Pick<AiSlot, "choices" | "synonyms">): EnumSentenceMatch[] | null {
  const words = wordsOf(sentence);
  const hits: EnumSentenceMatch[] = [];
  for (const choice of slot.choices ?? []) {
    const label = wordsOf(choice);
    const phrases = [choice, ...(slot.synonyms !== undefined && Object.hasOwn(slot.synonyms, choice) ? slot.synonyms[choice]! : [])];
    for (const phrase of phrases) {
      const run = wordsOf(phrase);
      for (const i of phraseStarts(words, run)) {
        // review #1070 M2: khớp nhờ chữ không dấu HAI NGHĨA ("moi quy") ⇒ cả câu không đủ chắc ⇒ hỏi.
        // re-review #1070 LOW-1: miễn khi đoạn chữ khớp TRỌN nhãn ("hang quy" — cũng là cách viết khác dấu "hàng quý" khai trong Sổ); chốt chỉ
        // cho cụm đồng nghĩa ngắn ("moi quy").
        const seg = words.slice(i, i + run.length);
        const isFullLabel = seg.length === label.length && label.every((l, k) => sameWord(seg[k]!, l));
        if (!isFullLabel && seg.some(isAmbiguousPlain)) return null;
        hits.push({ choice, start: words[i]!.start, end: words[i + run.length - 1]!.end });
      }
    }
  }
  const swallowed = (h: EnumSentenceMatch): boolean =>
    hits.some((o) => o.choice !== h.choice && o.start <= h.start && h.end <= o.end && o.end - o.start > h.end - h.start);
  return hits.filter((h) => !swallowed(h));
}

// ─── Phủ quyết theo NGHĨA ô `fromSentence` (review #1070 H2/M1 · bước 7) — MỘT vị-từ cho lưới câu, resolver và phép neo ───────────────────────
/** Từ đếm (bỏ dấu) — chữ số hoặc số bằng chữ. "một"/"1" là đếm MỘT lần. */
const COUNT_WORDS: ReadonlySet<string> = new Set(["mot", "hai", "ba", "bon", "tu", "nam", "sau", "bay", "tam", "chin", "muoi", "vai", "may", "nhieu"]);
const isCount = (k: string): boolean => /^\d+$/u.test(k) || COUNT_WORDS.has(k);
const isOne = (k: string): boolean => k === "1" || k === "mot";

/**
 * 🔴 review #1070 H2 + re-review HIGH/MED — chu kỳ: "<đếm> <ĐƠN VỊ>" với đếm ≠ 1 ("mỗi tháng 2 lần", "thu 2 ĐỢT", "2 kỳ"), "<1/một> <đơn vị>
 * thôi", hoặc "chỉ … <1/một> <đơn vị>" (cửa sổ ngắn) ⇒ câu không nói MỘT lần mỗi kỳ ⇒ không điền. Đơn vị đếm là tập ĐÓNG, so bằng `sameWord`
 * (dấu phải khớp khi cả hai phía có dấu; gõ không dấu thì nới): "đợt" là từ của CHÍNH app. "Nam, Lan" (tên) trùng "năm lần" ⇒ hỏi thừa, chiều an toàn.
 */
const COUNT_UNITS: readonly Word[] = wordsOf("lần đợt kỳ lượt bận");
const ONLY_WORD: Word = wordsOf("chỉ")[0]!;
const ONLY_WINDOW = 3;
function countsTimesOtherThanOnce(text: string): boolean {
  const w = wordsOf(text);
  return w.some((u, i) => {
    if (i === 0 || !COUNT_UNITS.some((c) => sameWord(u, c)) || !isCount(w[i - 1]!.key)) return false;
    if (!isOne(w[i - 1]!.key)) return true;
    if (w[i + 1]?.key === "thoi") return true;
    return w.slice(Math.max(0, i - 1 - ONLY_WINDOW), i - 1).some((x) => sameWord(x, ONLY_WORD));
  });
}

/** 🔴 review #1070 M1 (QĐ điều phối) — loại nhóm: ngữ cảnh người lớn/khoá học. So theo `sameWord` (dấu phải khớp khi cả hai phía có dấu). */
const GROUP_TYPE_VETO_PHRASES: readonly string[] = ["cựu", "đại học", "cao học", "người lớn", "khoá"];
function adultOrCohortContext(text: string): boolean {
  return GROUP_TYPE_VETO_PHRASES.some((p) => sentenceHasEnumPhrase(text, p)) || normalizeEnumKey(text).split(" ").some((w) => /^k\d+$/u.test(w));
}

/** Phủ quyết theo NGHĨA của ô — vét cạn theo `AI_SLOT_FROM_SENTENCE` (thiếu khoá ⇒ typecheck đỏ). */
const FROM_SENTENCE_VETOES: Readonly<Record<AiSlotFromSentence, (text: string) => boolean>> = {
  "sole-frequency": countsTimesOtherThanOnce,
  "sole-group-type": adultOrCohortContext,
  // H08(a): nghĩa KHẲNG ĐỊNH không phủ quyết theo ngữ cảnh — nó CHỈ mở khi cả câu thuộc khuôn ĐÓNG (`parseMoneyArrived`), tức liệt kê cái được
  // PHÉP thay vì cái bị cấm. Phủ định/nghi vấn vẫn chặn ở `hasNegationOrDoubt` (vế đầu của `fromSentenceBlocks`) và nhánh `resolveHedged`.
  "money-received": () => false,
};

// ─── H08(a) "tiền của X đã về" — khuôn ĐÓNG cho nghĩa `money-received` (US-AIV2-0112 · @UC0199 · QĐ PO 30/09 P9) ─────────────────────────────
/**
 * 🔴🔴 US-AIV2-0112 H08(a) — MỘT khuôn cho hai nửa: resolver của ô (`affirmedChoiceOf`, mọi lượt) và lưới câu của Người làm
 * (`fillReceivedDecisionFromSentence`, lượt mở, còn đòi tên X đúng chỗ = ô người Phiếu đã gạch). §3.7: đây là bước (3) — XÁC NHẬN tiền đã
 * nhận — nên khuôn cố ý HẸP; lẫn MỘT chữ ngoài khuôn ⇒ `null` ⇒ hỏi như develop. Ba dạng (dấu câu bỏ qua):
 *
 *   A  [ok] [NGÔI-1|anh|chị|em…] {đã|vừa|mới|cũng}* nhận [được] [đủ] tiền [của] [TÊN [chuyển|gửi|ck…] [về|vào]] {rồi|xong}* {trợ từ}*
 *   B  [ok] tiền [của] [TÊN] {đã…}* [chuyển|gửi|ck… [khoản]] về|vào [tài khoản|tk|quỹ|ví [của] [NGÔI-1|nhóm|quỹ|lớp]] [đủ] {rồi|xong}* {trợ từ}*
 *   C  [ok] TÊN {đã…}* chuyển|gửi|ck… [khoản] [tiền] {rồi|xong}* + dạng B KHÔNG tên
 *
 * và câu nói việc ĐÃ xong: A — "được", "đã/vừa/mới" hoặc "rồi/xong"; B/C — "đã/vừa/mới" hoặc "rồi/xong" ở vế tiền về. KHÔNG có trong khuôn (⇒ hỏi):
 * số tiền (số lấy từ LỜI BÁO, không từ câu) · "thu/cầm/giữ" · "hộ/giùm/thay" · "tin nhắn…" (không phải tiền) · "Quân chuyển tiền rồi" (chỉ là LỜI
 * KHAI, trạng thái 2) · "sẽ/đang/khi nào/nếu" · thêm một việc khác · mọi chữ của lựa chọn "từ chối". TÊN = 1–4 chữ không thuộc khuôn, không
 * có số, không phải đại từ/chữ chung (luật (c)); xưng hô đứng trước (chị Lan, bé Na) thuộc TÊN.
 * Trả `MoneyArrived` hoặc `null` khi ngoài khuôn. Resolver chỉ nhận câu KHÔNG tên (review #1076 H1); lưới câu đòi CÓ tên, so với ô người, rồi đưa
 * resolver `withoutName`. "bị trả về · hoàn · rút · nhầm" và tiền không phải quỹ ("lương, điện, phạt…") ⇒ ngoài khuôn.
 */
export function parseMoneyArrived(text: string): MoneyArrived | null {
  const nfc = text.normalize("NFC");
  const w = wordsOf(nfc);
  const r = parseMoneyArrivedWords(w);
  if (r === null) return null;
  if (r.name === null) return { name: null, core: null, withoutName: nfc.replace(/\s+/gu, " ").trim() };
  const slice = (a: number, b: number): string => nfc.slice(w[a]!.start, w[b - 1]!.end);
  const n = r.name;
  // Câu BỎ đoạn tên ("của" đứng trước + xưng hô + tên + "lớp mình" đứng sau): chữ CỦA CÂU, đúng thứ tự — tên đã thuộc ô người.
  return { name: slice(n.start, n.end), core: slice(n.core, n.end), withoutName: cutWords(nfc, w, [n.cut]) };
}

/** Chữ `nfc` BỎ các đoạn TỪ `cuts` (`[từ, tới)` theo chỉ số từ; chồng nhau được): chữ của câu, đúng thứ tự, gộp khoảng trắng, không để dấu câu mồ côi. */
function cutWords(nfc: string, w: readonly Word[], cuts: readonly (readonly [number, number])[]): string {
  let out = "";
  let pos = 0;
  for (const [a, b] of [...cuts].sort((x, y) => x[0] - y[0])) {
    const from = a === 0 ? 0 : w[a]!.start;
    if (from > pos) out += nfc.slice(pos, from) + " ";
    pos = Math.max(pos, b >= w.length ? nfc.length : w[b]!.start);
  }
  return (out + nfc.slice(pos))
    .replace(/\s+/gu, " ")
    .replace(/\s+([,.;!])/gu, "$1")
    .replace(/^[\s,.;!]+/u, "")
    .trim();
}

/**
 * Kết quả `parseMoneyArrived`: `name` = TÊN người trả kể cả xưng hô đứng trước ("chị Lan"), `core` = bỏ xưng hô ("Lan"); cả hai `null` khi câu
 * không nêu tên. `withoutName` = câu BỎ đoạn tên (kể cả "của" trước và "lớp mình" sau) — chữ lưới đưa resolver (review #1076 H1).
 */
export interface MoneyArrived {
  readonly name: string | null;
  readonly core: string | null;
  readonly withoutName: string;
}

/** Đoạn TÊN trong câu (chỉ số TỪ): `start` (kể cả xưng hô) · `core` (đầu phần tên) · `end` · `cut` = đoạn bỏ đi khi đưa resolver. */
interface NameRange {
  readonly start: number;
  readonly core: number;
  readonly end: number;
  readonly cut: readonly [number, number];
}

const MA = (s: string): readonly Word[] => wordsOf(s);
const MA_LEAD = MA("ok oke okay ừ ừm vâng");
const MA_SELF = MA("anh chị em cô chú bác");
const MA_PAST = MA("đã vừa mới");
const MA_ADV = [...MA_PAST, ...MA("cũng")];
const MA_DONE = MA("rồi xong");
const MA_PAY = MA("chuyển gửi ck đóng nộp");
const MA_ARRIVE = MA("về vào");
const MA_DEST_ONE = MA("tk quỹ ví");
const MA_OWNER = MA("nhóm quỹ lớp đội");
const MA_TAIL = MA("nhé nha nhe nhá nhen ạ đó ơi em anh chị luôn");
export const MA_HONORIFIC = MA("anh chị em cô chú bác bạn bé con cháu ông bà");
/** 🔴 AI-REDUNDANT-ASK (@UC0199 @UC0200): "mẹ/nhà/bố" NGAY trước "bé/con/cháu" là XƯNG HÔ của tên (như "chị") — tập chữ một nguồn với resolver tên; không có từ chỉ trẻ theo sau ("tiền nhà") ⇒ không. */
const MA_PARENT_OF = MA(PARENT_OF_WORDS.join(" "));
const MA_CHILD = MA(CHILD_WORDS.join(" "));
const maKinLead = (w: readonly Word[], j: number): boolean => maIs(w[j], MA_PARENT_OF) && maIs(w[j + 1], MA_CHILD);
/** Sau "lớp/nhóm" của tên ("Quân lớp mình"): chữ chỉ nhóm của người nói. */
const MA_OWNER_REF = MA("mình này ta tôi");
/**
 * Chữ không bao giờ là (một phần) TÊN: đại từ / chữ chung / "hộ" / thời thể — luật (c): chúng không chỉ ra MỘT người.
 * 🔴 review #1076 H1: + tiền đi NGƯỢC chiều / trả lại / nhầm ("bị trả về", "hoàn về", "rút về", "chuyển nhầm") và tiền KHÔNG phải quỹ ("tiền
 * lương/điện/nước/phạt/thưởng/lãi/học/nhà/cọc/thuê/hàng/ship về") ⇒ ngoài khuôn ⇒ hỏi. Tập ĐÓNG, chỉ THÊM.
 */
const MA_NOT_NAME = MA(
  "nó họ ai ấy đó này kia người ta mọi cả hết tất hộ giùm dùm thay sẽ đang định sắp muốn khi nếu thì lúc bao giờ nào " +
    "bị được hoàn trả rút nhầm lộn lương điện nước phạt thưởng lãi học nhà cọc thuê hàng ship",
);
const [W_TIEN, W_CUA, W_NHAN, W_DUOC, W_DU, W_TAI, W_KHOAN] = MA("tiền của nhận được đủ tài khoản");
/**
 * PCD-HARDEN: chữ THỜI GIAN không bao giờ là TÊN ("tiền đầu tháng", "tiền tháng 9"). So ĐÚNG chữ CÓ DẤU (không nới như `sameWord`): gõ không dấu
 * "thang" có thể là Thắng, "tuan" là Tuấn ⇒ vẫn đọc là tên ⇒ Người làm không resolve ra người trả ⇒ hỏi (chiều an toàn).
 */
const MA_TIME_ACCENTED: ReadonlySet<string> = new Set(["tháng", "tuần", "hôm", "đầu", "cuối", "sáng", "chiều", "tối", "nay", "qua"]);
const isTimeWord = (x: Word): boolean => !x.plain && MA_TIME_ACCENTED.has(x.accented);
/** Mọi chữ của khuôn — TÊN dừng ở chữ đầu tiên thuộc tập này. */
const MA_GRAMMAR: readonly Word[] = [...MA_ADV, ...MA_DONE, ...MA_PAY, ...MA_ARRIVE, ...MA_DEST_ONE, W_TIEN!, W_CUA!, W_NHAN!, W_DUOC!, W_DU!, W_TAI!, W_KHOAN!];
const MA_NAME_MAX = 4;

const maIs = (w: Word | undefined, set: readonly Word[]): boolean => w !== undefined && set.some((x) => sameWord(w, x));
/** AI-REDUNDANT-ASK (@UC0199): so chữ câu với TẬP CHỮ viết đúng chính tả ("bao" của "bao giờ" không nuốt "Bảo") — luật ở `sameListedWord`. */
const maIsListed = (w: Word | undefined, set: readonly Word[]): boolean => w !== undefined && set.some((x) => sameListedWord(w, x));
const maOne = (w: Word | undefined, lit: Word | undefined): boolean => w !== undefined && lit !== undefined && sameWord(w, lit);
/** TÊN dừng ở chữ của khuôn, ở "lớp/nhóm/đội" (review #1076 M4: "Quân lớp mình" là Quân) và ở trợ từ cuối. */
const maNameStop = (w: Word | undefined): boolean => maIs(w, MA_GRAMMAR) || maIs(w, MA_OWNER) || maIs(w, MA_TAIL);
/** Sau TÊN ở `k`: tuỳ chọn "lớp|nhóm|đội [mình|này…]" ("Quân lớp mình" là Quân) — vị trí sau đoạn đó (thuộc đoạn bỏ khi bỏ tên). */
function ownerSuffixEnd(w: readonly Word[], k: number): number {
  if (!maIs(w[k], MA_OWNER) || maOne(w[k], W_TIEN)) return k;
  const q = k + 1;
  return w[q] !== undefined && (maIs(w[q], MA_OWNER_REF) || isFirstPersonPronoun(w[q]!.accented)) ? q + 1 : q;
}

/**
 * TÊN bắt đầu ở `i` (`cutFrom` = đầu đoạn bỏ, gồm "của" đứng trước nếu có): xưng hô* + 1..4 chữ, rồi tuỳ chọn "lớp|nhóm|đội [mình|này…]".
 * Trả `NameRange` hoặc `null`.
 */
function maName(w: readonly Word[], i: number, cutFrom: number = i): NameRange | null {
  let j = i;
  while ((maIs(w[j], MA_HONORIFIC) || maKinLead(w, j)) && j + 1 < w.length && !maNameStop(w[j + 1])) j += 1;
  let k = j;
  while (k < w.length && k - j < MA_NAME_MAX && !maNameStop(w[k])) {
    const x = w[k]!;
    // Số · đại từ/chữ chung/tiền không phải quỹ · ngôi 1 · xưng hô trần · từ PHỦ ĐỊNH (`isNegationWord`, một nguồn với chốt phủ định).
    if (/\d/u.test(x.key) || maIsListed(x, MA_NOT_NAME) || isTimeWord(x) || isFirstPersonPronoun(x.accented) || maIs(x, MA_HONORIFIC) || isNegationWord(x)) return null;
    k += 1;
  }
  if (k === j) return null;
  return { start: i, core: j, end: k, cut: [cutFrom, ownerSuffixEnd(w, k)] };
}

/** 🔴 AI-REDUNDANT-ASK (@UC0199): PHẦN TÊN — bỏ xưng hô/người nhà-của-bé đầu ("nhà bé Gia Bảo" ⇒ "Gia Bảo"), cùng vòng bỏ đầu của `maName`; bỏ hết ⇒ chữ vào. */
export function nameCoreOf(text: string): string {
  const nfc = text.normalize("NFC").trim();
  const w = wordsOf(nfc);
  let j = 0;
  while ((maIs(w[j], MA_HONORIFIC) || maKinLead(w, j)) && j + 1 < w.length) j += 1;
  return j === 0 || j >= w.length ? nfc : nfc.slice(w[j]!.start).trim();
}

/** Vế "tiền … về/vào …" từ `i` (tên tuỳ `allowName`). Trả vị trí sau vế + tên + đã xong chưa, hoặc `null`. */
function maArrivalClause(w: readonly Word[], i: number, allowName: boolean): { end: number; name: NameRange | null; done: boolean } | null {
  let j = i;
  if (!maOne(w[j], W_TIEN)) return null;
  j += 1;
  let name: NameRange | null = null;
  if (allowName) {
    const cuaAt = j;
    const hasCua = maOne(w[j], W_CUA);
    if (hasCua) j += 1;
    if (!maIs(w[j], MA_GRAMMAR)) {
      name = maName(w, j, cuaAt);
      if (name === null) return null;
      j = name.cut[1];
    } else if (hasCua) return null;
  }
  let done = false;
  while (maIs(w[j], MA_ADV)) {
    done ||= maIs(w[j], MA_PAST);
    j += 1;
  }
  if (maIs(w[j], MA_PAY)) {
    j += 1;
    if (maOne(w[j], W_KHOAN)) j += 1;
  }
  if (!maIs(w[j], MA_ARRIVE)) return null;
  j += 1;
  const destEnd = maDest(w, j);
  if (destEnd !== null) j = destEnd;
  if (maOne(w[j], W_DU)) j += 1;
  while (maIs(w[j], MA_DONE)) {
    done = true;
    j += 1;
  }
  return { end: j, name, done };
}

/** "tài khoản" | tk | quỹ | ví, rồi tuỳ chọn [của] (NGÔI-1 | nhóm | quỹ | lớp). */
function maDest(w: readonly Word[], i: number): number | null {
  let j = i;
  if (maOne(w[j], W_TAI) && maOne(w[j + 1], W_KHOAN)) j += 2;
  else if (maIs(w[j], MA_DEST_ONE)) j += 1;
  else return null;
  const k = maOne(w[j], W_CUA) ? j + 1 : j;
  if (w[k] !== undefined && (isFirstPersonPronoun(w[k]!.accented) || maIs(w[k], MA_OWNER) || maIs(w[k], MA_SELF))) return k + 1;
  return j;
}

/** Dạng A: [ngôi 1] {trạng từ}* nhận [được] [đủ] tiền [của] [TÊN [chuyển…] [về|vào]] {rồi}*. */
function maReceiverFirst(w: readonly Word[], i: number): { end: number; name: NameRange | null } | null {
  let j = i;
  if (w[j] !== undefined && (isFirstPersonPronoun(w[j]!.accented) || maIs(w[j], MA_SELF))) j += 1;
  let done = false;
  while (maIs(w[j], MA_ADV)) {
    done ||= maIs(w[j], MA_PAST);
    j += 1;
  }
  if (!maOne(w[j], W_NHAN)) return null;
  j += 1;
  if (maOne(w[j], W_DUOC)) {
    done = true;
    j += 1;
  }
  if (maOne(w[j], W_DU)) j += 1;
  if (!maOne(w[j], W_TIEN)) return null;
  j += 1;
  const cuaAt = j;
  const hasCua = maOne(w[j], W_CUA);
  if (hasCua) j += 1;
  let name: NameRange | null = null;
  // AI-REDUNDANT-ASK: trợ từ "nha" (không dấu) không nuốt "nhà" có dấu ("tiền của NHÀ bé Gia Bảo").
  if (w[j] !== undefined && !maIs(w[j], MA_GRAMMAR) && !maIsListed(w[j], MA_TAIL)) {
    name = maName(w, j, cuaAt);
    if (name === null) return null;
    j = name.cut[1];
    if (maIs(w[j], MA_PAY)) j += 1;
    if (maIs(w[j], MA_ARRIVE)) j += 1;
  } else if (hasCua) return null;
  while (maIs(w[j], MA_DONE)) {
    done = true;
    j += 1;
  }
  return done ? { end: j, name } : null;
}

/**
 * Dạng C: [TÊN] {trạng từ}* chuyển… [khoản] [tiền] {rồi}* + vế tiền về KHÔNG tên. TÊN tuỳ chọn: câu BỎ tên mà lưới đưa resolver
 * ("chuyển rồi, tiền về rồi") cũng là dạng C.
 */
function maPayerThenArrival(w: readonly Word[], i: number): { end: number; name: NameRange | null } | null {
  let name: NameRange | null = null;
  let j = i;
  if (!maIs(w[j], MA_PAY) && !maIs(w[j], MA_ADV)) {
    name = maName(w, i);
    if (name === null) return null;
    j = name.cut[1];
  }
  while (maIs(w[j], MA_ADV)) j += 1;
  if (!maIs(w[j], MA_PAY)) return null;
  j += 1;
  if (maOne(w[j], W_KHOAN)) j += 1;
  if (maOne(w[j], W_TIEN) && !maIs(w[j + 1], MA_ARRIVE) && !maIs(w[j + 1], MA_ADV)) j += 1;
  while (maIs(w[j], MA_DONE)) j += 1;
  const arrival = maArrivalClause(w, j, false);
  if (arrival === null || !arrival.done) return null;
  return { end: arrival.end, name };
}

function parseMoneyArrivedWords(w: readonly Word[]): { name: NameRange | null } | null {
  if (w.length === 0) return null;
  const start = maIs(w[0], MA_LEAD) ? 1 : 0;
  const tries = [
    maReceiverFirst(w, start),
    ((): { end: number; name: NameRange | null } | null => {
      const b = maArrivalClause(w, start, true);
      return b !== null && b.done ? b : null;
    })(),
    maPayerThenArrival(w, start),
  ];
  for (const t of tries) {
    if (t === null) continue;
    let j = t.end;
    while (maIs(w[j], MA_TAIL)) j += 1;
    if (j === w.length) return { name: t.name };
  }
  return null;
}

/**
 * Lựa chọn KHẲNG ĐỊNH (duy nhất được chốt) mà chữ nói ra theo nghĩa KHẲNG ĐỊNH của ô, hoặc `null`. Chỉ nghĩa `money-received`.
 * Chữ nêu tên không bao giờ tới đây: chốt tên (`decisionPersonRefs`, review #1076 H1 → PCD-HARDEN) đứng đầu `resolveEnumSlot`, cho MỌI nhánh.
 */
function affirmedChoiceOf(slot: Pick<AiSlot, "fromSentence" | "guardedChoices">, raw: string): string | null {
  if (slot.fromSentence !== "money-received" || slot.guardedChoices?.length !== 1) return null;
  return parseMoneyArrived(raw) !== null ? slot.guardedChoices[0]! : null;
}

// ─── PCD-HARDEN — chữ quyết định NÊU TÊN người (US-AIV2-0112 PCD-SYNONYM-NAMED · @UC0199 @UC0320) ──────────────────────────────────────────────
/**
 * 🔴🔴 Tên người mà chữ QUYẾT ĐỊNH (ô `money-received`) nêu ra + chữ BỎ tên — MỘT nguồn cho: `resolveEnumSlot` (nêu tên ⇒ hỏi, MỌI nhánh, CẢ HAI
 * lựa chọn; resolver không biết lời báo đang xét của ai) và Người làm (`decisionTextForPayer`, api: mọi tên phải resolve ra CHÍNH người trả của
 * lời báo đang xét ⇒ đưa resolver `without`). Lỗ #1036: đang xét Quân, "anh nhận được tiền của Lan rồi" ⇒ đồng nghĩa ⇒ xác nhận lời báo của Quân.
 * TÊN = [xưng hô]* + 1–4 chữ ngoài tập đóng (khuôn tiền về · cụm của ô · `DP_FILLER` · đại từ · phủ định · thời gian · số) + tuỳ chọn "lớp mình",
 * chỉ đọc ở NĂM chỗ đứng: (1) người trả của khuôn `parseMoneyArrived` · (2) sau "của/cho/từ" (không phải "từ chối") · (3) "tiền X" + việc tiền
 * đi/về · (4) sau cụm lựa chọn/đồng nghĩa [+ tiền|khoản|lời báo] · (5) đầu câu trước động từ trả tiền. Đọc oan chữ thường là tên ⇒ không resolve
 * ra người trả ⇒ HỎI (chiều an toàn); cách gọi tên ngoài năm chỗ ⇒ thẻ vẫn in tên + số của lời báo, vẫn phải bấm Đồng ý (README PCD-HARDEN §4).
 */
export interface DecisionPersonRefs {
  /** Tên được nêu (kể cả xưng hô đứng trước: "chị Lan"), theo thứ tự xuất hiện, không trùng. */
  readonly names: readonly string[];
  /** Chữ BỎ mọi đoạn tên (kể cả "của/cho/từ" đứng trước, "lớp mình" đứng sau). Không tên ⇒ chính chữ vào. */
  readonly without: string;
}

/**
 * Chữ KHÔNG BAO GIỜ là (một phần) TÊN trong chữ quyết định, ngoài chữ của khuôn + cụm của ô. Tập ĐÓNG và cố ý NHỎ: chữ lạ bị đọc thành tên thì
 * Người làm không resolve được ra người trả ⇒ HỎI (chiều an toàn); chữ thừa trong tập thì một cách gọi tên bị sót (gõ không dấu "bao" ≡ "báo").
 * Chỉ thêm chữ đo được đứng ngay sau một cụm quyết định ("xác nhận giúp em", "từ chối vì…", "duyệt cái này").
 */
export const DP_FILLER = MA("lời báo khoản cái cho từ đi giúp với vì do thôi thấy sai số tìm kiểm tra giao dịch đối chiếu");
/** Sau "tiền X": chỉ đọc X là tên khi tiếp theo là việc tiền đi/về ("tiền Lan đã vào", "tiền Lan về") — "tiền tháng 9", "tiền mặt" không phải tên. */
const DP_AFTER_MONEY_NAME: readonly Word[] = [...MA_ADV, ...MA_PAY, ...MA_ARRIVE, ...MA_DONE];
const DP_PREPOSITION = MA("của cho từ");
const [W_TU, W_CHOI] = MA("từ chối");

export function decisionPersonRefs(text: string, slot: Pick<AiSlot, "choices" | "synonyms">): DecisionPersonRefs {
  const nfc = text.normalize("NFC");
  const w = wordsOf(nfc);
  const phrases = dpPhrases(slot);
  const isFiller = dpFillerOf(phrases);
  const found: NameRange[] = [];
  const add = (r: NameRange | null): void => void (r !== null && found.push(r));
  add(parseMoneyArrivedWords(w)?.name ?? null); // 1
  const lead = maIs(w[0], MA_LEAD) ? 1 : 0;
  const head = dpName(w, lead, isFiller);
  if (head !== null) {
    let j = head.cut[1];
    while (maIs(w[j], MA_ADV)) j += 1;
    if (maIs(w[j], MA_PAY)) add(head); // 5
  }
  w.forEach((x, i) => {
    if (maIs(x, DP_PREPOSITION) && !(maOne(x, W_TU) && maOne(w[i + 1], W_CHOI))) add(dpName(w, i + 1, isFiller, i)); // 2
    else if (maOne(x, W_TIEN)) {
      const n = dpName(w, i + 1, isFiller);
      if (n !== null && maIs(w[n.cut[1]], DP_AFTER_MONEY_NAME)) add(n); // 3
    }
  });
  for (const p of phrases) for (const s of phraseStarts(w, p)) add(dpName(w, s + p.length + dpObjectLength(w, s + p.length), isFiller)); // 4
  if (found.length === 0) return { names: [], without: text };
  const names = [...new Set(found.sort((a, b) => a.start - b.start).map((n) => nfc.slice(w[n.start]!.start, w[n.end - 1]!.end)))];
  return { names, without: cutWords(nfc, w, found.map((n) => n.cut)) };
}

const [W_LOI, W_BAO] = MA("lời báo");
/** Sau cụm quyết định: "tiền" · "khoản" · "lời báo" đứng trước tên ("xác nhận tiền Lan", "duyệt lời báo Lan") — số từ bỏ qua. */
function dpObjectLength(w: readonly Word[], i: number): number {
  if (maOne(w[i], W_TIEN)) return 1;
  if (maOne(w[i], W_KHOAN)) return 1;
  return maOne(w[i], W_LOI) && maOne(w[i + 1], W_BAO) ? 2 : 0;
}

/**
 * 🔴 review #1081 H1 — LẬT CHIỀU: MỌI chuỗi chữ-tên liền nhau của câu (`dpNameWord`: ngoài cụm lựa chọn/đồng nghĩa · `DP_FILLER` · ngữ pháp khuôn ·
 * đầu/cuối câu · đại từ · xưng hô · phủ định · thời gian có dấu · số), không cần chỗ đứng. Người làm (`decisionTextForPayer`) đòi mỗi chuỗi resolve ra
 * người trả của lời báo đang xét ⇒ tên ĐẦU câu / gọi-đáp ("Lan xác nhận", "nhận được rồi Lan ơi") không còn lọt; chữ lạ ⇒ hỏi (chiều an toàn).
 */
export function decisionNameRuns(text: string, slot: Pick<AiSlot, "choices" | "synonyms">): string[] {
  const nfc = text.normalize("NFC");
  const w = wordsOf(nfc);
  const isFiller = dpFillerOf(dpPhrases(slot));
  const runs: string[] = [];
  for (let i = 0; i < w.length; ) {
    let k = i;
    // AI-REDUNDANT-ASK: "mẹ/nhà" ngay trước "bé/con/cháu" là xưng hô (như "chị"), không phải một tên khác.
    while (k < w.length && dpNameWord(w[k]!, isFiller) && !maKinLead(w, k)) k += 1;
    if (k > i) runs.push(nfc.slice(w[i]!.start, w[k - 1]!.end));
    i = Math.max(k, i + 1);
  }
  return [...new Set(runs)];
}

const dpPhrases = (slot: Pick<AiSlot, "choices" | "synonyms">): Word[][] =>
  (slot.choices ?? []).flatMap((c) => [c, ...(slot.synonyms !== undefined && Object.hasOwn(slot.synonyms, c) ? slot.synonyms[c]! : [])]).map(wordsOf);
const dpFillerOf =
  (phrases: readonly Word[][]) =>
  (x: Word): boolean =>
    maIs(x, DP_FILLER) || phrases.some((p) => maIs(x, p));
/** Một chữ CÓ THỂ là (một phần) tên — một vị-từ cho `decisionPersonRefs` (5 chỗ đứng) và `decisionNameRuns` (mọi chỗ). */
export function dpNameWord(x: Word, isFiller: (x: Word) => boolean): boolean {
  return !/\d/u.test(x.key) && !maNameStop(x) && !maIsListed(x, MA_NOT_NAME) && !isTimeWord(x) && !maIs(x, MA_LEAD) && !maIs(x, MA_HONORIFIC) && !isFirstPersonPronoun(x.accented) && !isNegationWord(x) && !isFiller(x);
}

/** TÊN bắt đầu ở `i` cho `decisionPersonRefs` (`cutFrom` = đầu đoạn bỏ): xưng hô* + 1–4 chữ-tên, tuỳ chọn "lớp/nhóm/đội [mình]". Không chữ-tên ⇒ `null`. */
function dpName(w: readonly Word[], i: number, isFiller: (x: Word) => boolean, cutFrom: number = i): NameRange | null {
  let j = i;
  while (maIs(w[j], MA_HONORIFIC) || maKinLead(w, j)) j += 1;
  let k = j;
  while (k < w.length && k - j < MA_NAME_MAX && dpNameWord(w[k]!, isFiller) && !maKinLead(w, k)) k += 1;
  if (k === j) return null;
  return { start: i, core: j, end: k, cut: [cutFrom, ownerSuffixEnd(w, k)] };
}

/**
 * 🔴 H02 bước 7 (ảnh SAU `H02-tuan-2lan`: Phiếu THẬT gạch `chuKy` = "mỗi tháng" từ "mỗi tháng 2 lần…" ⇒ đồng nghĩa ⇒ hằng tháng) — ô khai
 * `fromSentence` + chữ mang phủ định/nghi vấn HOẶC ngữ cảnh mà NGHĨA ô biết là lừa ⇒ không chọn gì. Bản đầu chỉ lưới câu gọi phủ quyết; nhánh
 * span (nhãn · đồng nghĩa) và lượt trả lời thì không ⇒ chốt đặt sai chỗ. Nay: lưới câu (`fillEnumSlotsFromSentence`) · `resolveEnumSlot` (mọi
 * nhánh khớp) · phép neo (`enumSentenceNeedsWholeView` nới span ra CẢ câu để resolver thấy "2 lần" nằm ngoài span model gạch).
 */
export function fromSentenceBlocks(slot: Pick<AiSlot, "fromSentence">, text: string): boolean {
  return slot.fromSentence !== undefined && (hasNegationOrDoubt(text) || FROM_SENTENCE_VETOES[slot.fromSentence](text));
}

/**
 * Phép neo (`judgeSpan`) phải đưa CẢ CÂU cho resolver khi: ô có lựa chọn khẳng định được chốt (`guardedChoices`) và câu phủ định/nghi vấn, HOẶC ô
 * `fromSentence` và câu bị `fromSentenceBlocks`. Một vị-từ — `output.ts` không chép điều kiện.
 */
export function enumSentenceNeedsWholeView(slot: Pick<AiSlot, "type" | "guardedChoices" | "fromSentence">, sentence: string): boolean {
  if (slot.type !== "enum") return false;
  if ((slot.guardedChoices?.length ?? 0) > 0 && hasNegationOrDoubt(sentence)) return true;
  return fromSentenceBlocks(slot, sentence);
}
